"use client";

import React from "react";
import { 
  X, 
  Dumbbell, 
  Mail, 
  Phone, 
  Calendar, 
  Activity, 
  UserCheck, 
  Clock, 
  CreditCard,
  Trash2
} from "lucide-react";
import { GymMemberLead } from "@/lib/demos/gymDb";

interface GymDetailModalProps {
  member: GymMemberLead | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (id: number) => void;
}

export default function GymDetailModal({
  member,
  isOpen,
  onClose,
  onDelete,
}: GymDetailModalProps) {
  if (!isOpen || !member) return null;

  const sym = member.currency === "USD" ? "$" : "₹";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-stone-950 border border-stone-800 rounded-3xl max-w-xl w-full p-6 sm:p-8 relative shadow-2xl text-stone-100 space-y-6">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-stone-400 hover:text-white p-1.5 rounded-full hover:bg-stone-900 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-stone-800 pb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[10px] font-extrabold text-amber-400 uppercase tracking-wider">
                {member.stage}
              </span>
              <span className="text-xs text-stone-400">ID #{member.id}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white">
              {member.lead_name}
            </h2>
            <div className="flex flex-wrap items-center gap-3 text-xs text-stone-400 pt-1">
              <span className="flex items-center gap-1">
                <Mail className="w-3.5 h-3.5 text-stone-500" />
                {member.email}
              </span>
              {member.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5 text-stone-500" />
                  {member.phone}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800 space-y-1.5">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Primary Athletic Goal
            </span>
            <p className="font-semibold text-white">{member.fitness_goal}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800 space-y-1.5">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Target Membership Tier
            </span>
            <p className="font-semibold text-amber-400">{member.membership_tier}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800 space-y-1.5">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Assigned Performance Coach
            </span>
            <p className="font-semibold text-white">{member.assigned_coach || "Unassigned"}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800 space-y-1.5">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Tour / Orientation Schedule
            </span>
            <p className="font-semibold text-emerald-400">{member.tour_date || "Pending Booking"}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800 space-y-1.5 sm:col-span-2">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Biometric InBody 770 Diagnostic Record
            </span>
            <p className="font-semibold text-indigo-300">
              {member.inbody_score || "Awaiting scheduled baseline scan"}
            </p>
          </div>

          <div className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800 space-y-1.5">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Preferred Training Hour
            </span>
            <p className="font-semibold text-white">{member.preferred_time || "Flexible"}</p>
          </div>

          <div className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800 space-y-1.5">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Annual Dues ({member.currency})
            </span>
            <p className="font-bold text-amber-400 text-sm">
              {sym}{member.annual_dues.toLocaleString()}
            </p>
          </div>
        </div>

        {member.notes && (
          <div className="p-3.5 rounded-2xl bg-stone-900/40 border border-stone-800 text-xs space-y-1">
            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
              Staff Notes &amp; Special Considerations:
            </span>
            <p className="text-stone-300">{member.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-stone-800 text-xs">
          <button
            onClick={() => {
              if (confirm(`Remove member lead "${member.lead_name}"?`)) {
                onDelete(member.id);
                onClose();
              }
            }}
            className="text-red-400 hover:text-red-300 font-semibold flex items-center gap-1.5 px-3 py-2 rounded-xl hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete Lead</span>
          </button>

          <button
            onClick={onClose}
            className="py-2.5 px-6 rounded-xl bg-stone-800 hover:bg-stone-700 font-bold text-white transition-colors"
          >
            Close Dossier
          </button>
        </div>
      </div>
    </div>
  );
}
